/**
 * @mixin
 * this mixin is the basic mixin of all the regular ParamTypes
 * it should be used whenever a new ParamType is born
 *
 * it provides functionality of localValue and localValueDisplay (for constant values use)
 * and fieldRules which are the basic validation rules for the field
 */
import ParamWrapper from '../common/ParamWrapper';
import ParamPropMixin from './PropsMixins/ParamPropMixin';
import IsRequiredMixin from './ParamPropertiesMixins/IsRequiredMixin';

import isString from 'lodash/isString';
import ParamTypeMixinService from './param-type-mixin-service';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
export default {
	mixins: [ParamPropMixin, IsRequiredMixin],
	components: { ParamWrapper },
	props: {
		node: { type: Object },
		service: { type: Object },
		action: { type: Object },
		workflowVersion: { type: Object },
		readonly: { type: Boolean },
	},
	computed: {
		/**
		 * @returns the necessary props for ParamWrapper component
		 */
		wrapperProps() {
			return Object.keys(ParamWrapper.props).reduce((bindData, propKey) => ({ ...bindData, ...{ [propKey]: this[propKey] } }), {});
		},
		/**
		 * @returns the necessary listeners for ParamWrapper component
		 */
		wrapperListeners() {
			return { 'input-data-closed': () => {} };
		},
		/**
		 * the value of the param
		 */
		localValue: {
			get() {
				return this.hideRealValue ? '' : this.getParamForNode(this.param.key);
			},
			set(value) {
				this.$data._setLocalValueBeforeMiddlewares.forEach((middleware) => middleware());
				this.$emit('update-input-field', {
					map: { key: this.param.key, value },
					param: this.param,
					preventUpdateEditNode: this.$data._preventUpdateEditNode,
				});
			},
		},
		/**
		 * the label of the value of the param (if available) - used by constant values
		 */
		localValueDisplay: {
			get() {
				return this.getParamLabelForNode(this.param.key);
			},
			set(value) {
				this.$emit('update-input-field-label', {
					label: { key: this.param.key, value },
					param: this.param,
					preventUpdateEditNode: this.$data._preventUpdateEditNode,
				});
			},
		},
		preventUpdateEditNode: {
			// prevents sending update to backend
			set(value) {
				this.$data._preventUpdateEditNode = value;
			},
			get() {
				return this.$data._preventUpdateEditNode;
			},
		},
		isValueConstant() {
			return !isNullOrUndefinedOrEmpty(this.localValueDisplay);
		},
		isValueSingularColumn() {
			const localValue = this.getParamForNode(this.param.key); // we get the localValue as defined in the getter, so we have the real sourceValue and not the computed one
			// we check if value is string and if it looks like '{{EXAMPLE}}' and not classic '{{NODE_ID_EXAMPLE__EXAMPLE}}
			const columnValueRegex = /^{{(?:(?!__).)+}}$/;
			return isString(localValue) && columnValueRegex.test(localValue);
		},
		paramsMap() {
			return this.node.params_map || {};
		},
		paramsMapLabels() {
			return this.node.params_map_labels || {};
		},
		/**
		 * executes all callbacks that were registered to the current param
		 * if one is true, then localValue will be hidden
		 *
		 * to add callback, use addHideRealValueCallback method
		 * @returns rather localValue should be hidden from user
		 */
		hideRealValue() {
			/**
			 * if were switching between ThisFlow & Column tab, when param accepts to get single column param,
			 * Column tab acts differently then ThisFlow tab
			 *
			 * the way it works is that if at least one callback returns true, then we hide the real value of the local value
			 */
			return this.$data._hideRealValueCallbacks.some((callback) => callback());
		},
		/**
		 * the input element of the ParamType
		 * its the ParamType components responsibility to use setInputElement and provide its input's element
		 * @returns
		 */
		inputElement() {
			return this.$data._inputElement;
		},
	},
	methods: {
		getParamForNode(paramKey) {
			return paramKey in this.paramsMap ? this.paramsMap[paramKey] : null;
		},
		getParamLabelForNode(paramKey) {
			if (!this.paramsMap.hasOwnProperty(paramKey)) {
				return null;
			}
			return this.paramsMapLabels[paramKey];
		},
		/**
		 * method to add rules to the current field rules
		 * can be used by ParamType component itself or by other mixins
		 * @param {*} rule - the rule function to add
		 */
		addFieldRule(rule) {
			this.fieldRules.push(rule);
		},
		/**
		 * method to add hiding functionality to localValue on specific conditions
		 * @param {function} callback
		 */
		addHideRealValueCallback(callback) {
			this.$data._hideRealValueCallbacks.push(callback);
		},
		/**
		 * method to add middleware functions that will be executed before we change the localValue
		 * @param {function} middleware
		 */
		addSetLocalValueBeforeMiddleware(middleware) {
			this.$data._setLocalValueBeforeMiddlewares.push(middleware);
		},
		/**
		 * sets the inputElement of the ParamType
		 * @param {*} element
		 */
		setInputElement(element) {
			this.$data._inputElement = element;
		},
		applyLabelFromOptions(items) {
			ParamTypeMixinService.applyLabelFromOptions.call(this, items);
		},
		buildCommonRichTextBoxProps() {
			return {
				paramKey: this.param.key,
			};
		},
	},
	mounted() {
		ParamTypeMixinService.addParamFieldRules.call(this);
	},
	data() {
		return {
			fieldRules: [],
			_hideRealValueCallbacks: [],
			_setLocalValueBeforeMiddlewares: [],
			_inputElement: null,
			_preventUpdateEditNode: false,
		};
	},
};
